/**
 * tabbable.js — which controls can actually take focus right now (BC-4, F2).
 *
 * The focus trap computed its Tab ring from a selector alone, so a control
 * inside a hidden tab panel counted as the ring's first stop. Settings →
 * Account → Workspace → focus Close → Shift+Tab: the trap saw focus on "the
 * first focusable" (a hidden Account control), declined to wrap, and focus
 * stayed on Close. A selector says what COULD be focused; this also asks
 * whether it is on screen.
 *
 * The hidden test was private to `command-bar/focus-model.js`; it lives here
 * now so every focus manager answers the question the same way.
 *
 * jsdom loads no stylesheets, so computed `display` is only as good as the
 * inline styles and the structural checks (`hidden`, `inert`) carry the case
 * there. Browser suites assert the rendered truth.
 */

export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled]):not([aria-disabled="true"])',
  'input:not([disabled]):not([aria-disabled="true"]):not([type="hidden"])',
  'select:not([disabled]):not([aria-disabled="true"])',
  'textarea:not([disabled]):not([aria-disabled="true"])',
  'summary',
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"]):not([aria-disabled="true"])'
].join(', ');

function computedHides(el) {
  try {
    const view = el.ownerDocument?.defaultView;
    if (!view?.getComputedStyle) return false;
    const style = view.getComputedStyle(el);
    return style.display === 'none' || style.visibility === 'hidden';
  } catch {
    // Expected flow: a detached node in a realm without layout. Treat it as
    // visible and let the structural checks decide.
    return false;
  }
}

/**
 * True when `el` cannot receive focus from the keyboard because it, or an
 * ancestor up to (and including) `boundary`, is hidden or inert.
 * @param {Element} el
 * @param {Element} [boundary] stop walking at this ancestor (the trap container)
 */
export function isHiddenOrInert(el, boundary = null) {
  if (!el) return true;
  if (el.closest?.('[hidden], [inert]')) return true;
  for (let node = el; node && node.nodeType === 1; node = node.parentElement) {
    // `display: none` on an ancestor does not show up in the child's computed
    // display, so every level is asked. Bounded by the container.
    if (computedHides(node)) return true;
    if (node === boundary) break;
  }
  return false;
}

/**
 * The controls in `container` that Tab would actually visit, in DOM order.
 * @param {Element} container
 * @returns {HTMLElement[]}
 */
export function getTabbables(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => !isHiddenOrInert(el, container)
  );
}
